import React, { useEffect, useMemo, useRef, useState } from 'react';

import { LuMessageSquare, LuSmartphone, LuUsers, LuMessageCircle, LuBookOpen, LuSmile } from 'react-icons/lu';







const EMPTY_TIMINGS = [];



const normalise = word => word.toLowerCase().replace(/[’‘]/g, "'").replace(/[^\p{L}\p{N}']/gu, '');



// Keep measured speech cues; estimate only script tokens absent from the ASR.



function buildTimeline(narration, wordTimings, duration) {



  const tokens = [...narration.matchAll(/(\S+)(\s*)/g)];



  const script = tokens.map(token=>token[1]);



  // Estimate speech and pauses separately when measured words are unavailable.



  // Paragraph breaks give each narrated detail time to remain highlighted.



  const speechSeconds = .4;



  const pauses = tokens.map(([_,word,space])=>



    (/[.!?]$/.test(word)?.35:/[,;:]$/.test(word)?.12:0) + (/\n\s*\n/.test(space)?.65:0));



  const weights = pauses.map(pause=>speechSeconds+pause);



  const spoken = (Array.isArray(wordTimings) ? wordTimings : []).filter(item =>



    item && typeof item.word === 'string' && Number.isFinite(item.start) && Number.isFinite(item.end) && item.start >= 0 && item.end >= item.start);



  const length = duration > 0 ? duration : spoken.length ? spoken[spoken.length - 1].end : weights.reduce((sum,weight)=>sum+weight,0);



  const mapped = Array(script.length).fill(null);



  if (spoken.length) {



    const rows = script.length + 1, columns = spoken.length + 1;



    const scores = new Uint16Array(rows * columns);



    const source = script.map(normalise), audio = spoken.map(item => normalise(item.word));



    for (let i = 1; i < rows; i += 1) for (let j = 1; j < columns; j += 1) {



      scores[i * columns + j] = source[i - 1] && source[i - 1] === audio[j - 1]



        ? scores[(i - 1) * columns + j - 1] + 1



        : Math.max(scores[(i - 1) * columns + j], scores[i * columns + j - 1]);



    }



    let i = script.length, j = spoken.length;



    while (i > 0 && j > 0) {



      if (source[i - 1] && source[i - 1] === audio[j - 1]) {



        mapped[i - 1] = { ...spoken[j - 1], measured: true }; i -= 1; j -= 1;



      } else if (scores[(i - 1) * columns + j] >= scores[i * columns + j - 1]) i -= 1;



      else j -= 1;



    }



  }



  const measured = mapped.filter(Boolean).length;



  for (let index = 0; index < mapped.length;) {



    if (mapped[index]) { index += 1; continue; }



    const from = index;



    while (index < mapped.length && !mapped[index]) index += 1;



    const start = from ? mapped[from - 1].end : 0;



    const end = Math.max(start, index < mapped.length ? mapped[index].start : length);



    const totalWeight = weights.slice(from,index).reduce((sum,weight)=>sum+weight,0);



    const scale = totalWeight ? (end-start)/totalWeight : 0;



    let cursor = start;



    for (let k = from; k < index; k += 1) {



      mapped[k] = {start:cursor,end:cursor+speechSeconds*scale,measured:false};



      cursor += weights[k]*scale;



    }



  }



  return { words: mapped, mode: measured === script.length ? 'measured' : measured ? 'mixed' : 'estimated' };



}



function wordOffset(narration, phrase) {



  const at = narration.indexOf(phrase);



  if (at < 0) throw new Error(`Missing narration cue: ${phrase}`);



  return narration.slice(0, at).trim().split(/\s+/).filter(Boolean).length;



}



function cueTime(narration, timeline, phrase) {



  return timeline.words[wordOffset(narration, phrase)]?.start ?? Infinity;



}



function activeIndex(items, now) {



  let index = 0;



  items.forEach((item, i) => { if (now >= item.start) index = i; });



  return index;



}



// Keep the same timing-input aliases and stable timeline as What Is Tribe.



function useNarrationTimeline(narration, wordTimings, duration) {



  const timingKey = JSON.stringify(Array.isArray(wordTimings)



    ? wordTimings.map(item => [item?.word, item?.start, item?.end]) : []);



  return useMemo(() => buildTimeline(narration, wordTimings, duration), [narration, duration, timingKey]);



}



// Read the audio clock each frame. Typing adds character boundaries so it



// stays in sync with speech, pause, seeking, and playback rate changes.



function useLessonClock({ time, audioElement, audioRef, playbackId, audioStatus } = {}, timeline) {



  const boundaries = useMemo(() => [...timeline.words.map(word => word.start), ...(timeline.animationBoundaries ?? [])].sort((a,b)=>a-b), [timeline]);



  const sourceTime = Number.isFinite(time) ? Math.max(0, time) : 0;



  const parentAnchor = useRef({ time: sourceTime, stamp: null });



  const controller = useRef({ time: sourceTime, boundary: -2, playing: false, rate: 1, syncId: 0 });



  const [clock, setClock] = useState({ now: sourceTime, playing: audioStatus === 'Playing', rate: 1, syncId: 0, seeking: false });



  const boundaryAt = current => {



    let low = 0, high = boundaries.length;



    while (low < high) {



      const middle = (low + high) >>> 1;



      if (boundaries[middle] <= current + .005) low = middle + 1;



      else high = middle;



    }



    return low - 1;



  };



  useEffect(() => {



    const previous = parentAnchor.current;



    const stamp = performance.now();



    const elapsed = previous.stamp == null ? 0 : (stamp - previous.stamp) / 1000;



    const expected = previous.time + (controller.current.playing ? elapsed * controller.current.rate : 0);



    const seek = previous.stamp != null && Math.abs(sourceTime - expected) > .6;



    parentAnchor.current = { time: sourceTime, stamp };



    // The media element is authoritative when available; otherwise changes to



    // the parent's time keep the interpolated fallback clock anchored.



    if (!(audioElement ?? audioRef?.current) && seek) {



      controller.current.syncId += 1;



      controller.current.boundary = -2;



      controller.current.time = sourceTime;



      setClock({ now: sourceTime, playing: audioStatus === 'Playing', rate: 1,



        syncId: controller.current.syncId, seeking: true });



    }



  }, [sourceTime, audioElement, audioRef]);



  useEffect(() => {



    const media = audioElement ?? audioRef?.current;



    const playbackChanged = controller.current.playbackId !== playbackId;



    if (!media) {



      const stamp = performance.now();



      const anchor = parentAnchor.current;



      const heldTime = anchor.time + (controller.current.playing && anchor.stamp != null



        ? (stamp-anchor.stamp)/1000*controller.current.rate : 0);



      // Anchor pause/resume to the elapsed clock instead of resetting to zero.



      parentAnchor.current = {time:playbackChanged?sourceTime:heldTime,stamp};



    }



    let frame;



    const read = () => {



      const state = controller.current;



      const playing = media ? !media.paused && !media.ended : audioStatus === 'Playing';



      const rate = media ? media.playbackRate || 1 : 1;



      const anchor = parentAnchor.current;



      const current = media ? Math.max(0, media.currentTime || 0) : Math.max(0,



        anchor.time + (playing && anchor.stamp != null ? (performance.now() - anchor.stamp) / 1000 * rate : 0));



      const boundary = boundaryAt(current);



      return { current, boundary, playing, rate };



    };



    const update = (force = false, seek = false) => {



      const sample = read(), state = controller.current;



      const rateChanged = sample.rate !== state.rate;



      if (seek || rateChanged) state.syncId += 1;



      state.time = sample.current;



      if (force || seek || rateChanged || sample.boundary !== state.boundary || sample.playing !== state.playing) {



        state.boundary = sample.boundary;



        state.playing = sample.playing;



        state.rate = sample.rate;



        setClock({ now: sample.current, playing: sample.playing, rate: sample.rate,



          syncId: state.syncId, seeking: seek || rateChanged });



      }



      return sample.playing;



    };



    const tick = () => { if (update()) frame = requestAnimationFrame(tick); };



    const play = () => { cancelAnimationFrame(frame); update(true); frame = requestAnimationFrame(tick); };



    const stop = () => { cancelAnimationFrame(frame); update(true); };



    const seek = () => { update(true, true); };



    const rate = () => { update(true); };



    const sync = () => { update(); };



    if (media) {



      media.addEventListener('play', play); media.addEventListener('pause', stop);



      media.addEventListener('ended', stop); media.addEventListener('seeked', seek);



      media.addEventListener('ratechange', rate); media.addEventListener('timeupdate', sync);



    }



    if (controller.current.playbackId !== playbackId) {



      controller.current.playbackId = playbackId;



      controller.current.syncId += 1;



    }



    update(true);



    if (read().playing) frame = requestAnimationFrame(tick);



    return () => {



      cancelAnimationFrame(frame);



      if (media) {



        media.removeEventListener('play', play); media.removeEventListener('pause', stop);



        media.removeEventListener('ended', stop); media.removeEventListener('seeked', seek);



        media.removeEventListener('ratechange', rate); media.removeEventListener('timeupdate', sync);



      }



    };



  }, [audioElement, audioRef, playbackId, audioStatus, boundaries]);



  return clock;



}







// Phrase-cued motion matches What Is Tribe and Event Structure. Delays stay



// fixed between clock updates, so CSS completes the gentle rise smoothly.



function PhraseReveal({ as: Tag = 'h1', text, emphasis = [], clock, complete = false, delay = 0 }) {



  const words = text.split(/\s+/).filter(Boolean);



  const settled = useMemo(() => clock.seeking || (!clock.playing && clock.now > 0), [clock.syncId, text]);



  const strong = new Set();



  emphasis.forEach(phrase => {



    const offset = wordOffset(text, phrase);



    phrase.split(/\s+/).forEach((_, index) => strong.add(offset + index));



  });



  return <Tag aria-label={text}>{words.map((word, index) => <React.Fragment key={index}>



    <span className={`conversation-reveal-word ${strong.has(index) ? 'is-emphasized' : ''}`} aria-hidden="true" style={{



      animationDelay: `${complete || settled ? -10 : (delay + index * .095) / clock.rate}s`,



      animationDuration: `${.28 / clock.rate}s`,



      animationPlayState: complete || clock.playing ? 'running' : 'paused',



    }}>{word}</span>{index < words.length - 1 ? ' ' : ''}



  </React.Fragment>)}</Tag>;



}







const INTRO_CAPTIONS = [



  { key: 'welcome', cue: 'Now that we’ve covered', text: 'You’re ready to welcome guests.', emphasis: ['welcome guests.'] },



  { key: 'facilitation', cue: 'let’s talk about facilitation', text: 'Let’s talk about facilitation.', emphasis: ['facilitation.'] },



  { key: 'goals', cue: 'In this section', text: 'Help guests connect. Keep the experience on track.', emphasis: ['connect.', 'on track.'] },



];



const CONVERSATION_SCENES = [



  { key: 'principle', cue: 'The first key concept of facilitation', text: 'Never let an event feel aimless.', emphasis: ['Never', 'aimless.'] },



  { key: 'signals', cue: 'You’ll know it’s starting to drift', text: 'Notice when the energy drops.' },



  { key: 'restart', cue: 'To prevent that', text: 'Get a conversation going.', emphasis: ['conversation going.'] },



  { key: 'rules', cue: 'Ask questions', text: 'Get a conversation going.' },



  { key: 'closing', cue: 'Your energy can help', text: 'Your energy helps people loosen up and join in.', emphasis: ['Your energy'] },



];



const SIGNALS = [



  { cue: 'stuck in silence', icon: 'chat', text: 'Silence', detail: 'Guests are waiting for someone to start talking.' },



  { cue: 'scrolling on their phones', icon: 'phone', text: 'Phones out', detail: 'People are scrolling instead of joining in.' },



  { cue: 'hovering on the edges', endCue: 'hovering on the edges.', icon: 'people', text: 'On the edges', detail: 'Someone is hovering outside the conversation.' },



];



const RULES = [



  { cue: 'Ask questions', icon: 'question', text: 'Ask questions', detail: 'Give guests an easy way to join the conversation.' },



  { cue: 'share an interesting story', icon: 'story', text: 'Share a story', detail: 'Share something others can respond to.' },



  { cue: 'bring some humor', endCue: 'bring some humor into the moment.', icon: 'smile', text: 'Bring some humor', detail: 'Keep it natural and help the group relax.' },



];



function buildConversationCues(part, narration, timeline) {



  const scenes = (part === 0 ? INTRO_CAPTIONS : CONVERSATION_SCENES)



    .map(scene => ({ ...scene, start: cueTime(narration, timeline, scene.cue) }));



  const timeRows = source => source.map(row => {



    const endCue = row.endCue ?? row.cue;



    const last = wordOffset(narration, endCue) + endCue.split(/\s+/).length - 1;



    return { ...row, start: cueTime(narration, timeline, row.cue), end: timeline.words[last]?.end ?? Infinity };



  });



  const rows = part === 0 ? {} : { signals: timeRows(SIGNALS), rules: timeRows(RULES) };



  return { scenes, rows };



}







function conversationFocus(rows, now, complete = false) {



  const latest = rows.reduce((index, row, i) => now >= row.start ? i : index, -1);



  const equal = complete || now >= rows.at(-1).end;



  return { latest: complete ? rows.length - 1 : latest, equal, active: equal ? -1 : latest };



}



const POINT_ICONS = {

  chat: LuMessageSquare,

  phone: LuSmartphone,

  people: LuUsers,

  question: LuMessageCircle,

  story: LuBookOpen,

  smile: LuSmile,

};



function PointIcon({ type }) {

  const Icon = POINT_ICONS[type] ?? LuMessageSquare;

  return <Icon aria-hidden="true" focusable="false" />;

}



function ConversationRows({ kind, rows, clock, complete }) {



  const focus = conversationFocus(rows, clock.now, complete);



  return <ol className={`conversation-rules is-${kind} ${focus.equal ? 'is-equal' : ''}`} aria-label={kind === 'signals' ? 'Signs the event is drifting' : 'Ways to start a conversation'}>



    {rows.map((row, index) => {



      const visible = index <= focus.latest;



      const active = index === focus.active;



      return <li key={row.cue} className={`conversation-rule ${visible ? 'is-visible' : ''} ${active ? 'is-focused' : ''} ${visible && !active && !focus.equal ? 'is-muted' : ''}`} aria-hidden={visible ? undefined : true} aria-current={active ? 'step' : undefined}>



        <span className="conversation-rule-track" aria-hidden="true"><span className="conversation-rule-circle"><PointIcon type={row.icon} /></span><span className="conversation-rule-dot" /></span>



        {visible && <div className="conversation-rule-copy" key={`${row.cue}-${clock.syncId}`}>



          <PhraseReveal as="h2" text={row.text} clock={clock} complete={complete} />



          <PhraseReveal as="p" text={row.detail} clock={clock} complete={complete} delay={.12} />



        </div>}



      </li>;



    })}



  </ol>;



}







// This lesson owns its visuals, narration metadata, and summary.



export default function Conversation(props = {}) {



  const part = Math.max(0, Math.min(1, Math.trunc(Number(props.part) || 0)));



  const narration = Conversation.parts[part].narration;



  const speechTimeline = useNarrationTimeline(narration, props.wordTimings ?? props.words ?? EMPTY_TIMINGS, props.duration ?? 0);



  const cues = useMemo(() => buildConversationCues(part, narration, speechTimeline), [part, narration, speechTimeline]);



  const timeline = useMemo(() => ({ ...speechTimeline, animationBoundaries: Object.values(cues.rows).flatMap(rows => [rows.at(-1).end]) }), [speechTimeline, cues]);



  const clock = useLessonClock({ ...props, playbackId: `${props.playbackId ?? 0}:${part}` }, timeline);



  const complete = props.audioStatus === 'Complete' || Boolean((props.audioElement ?? props.audioRef?.current)?.ended);



  const scene = cues.scenes[complete ? cues.scenes.length - 1 : activeIndex(cues.scenes, clock.now)];



  const listKind = scene.key === 'closing' ? 'rules' : scene.key;



  const list = cues.rows[listKind];



  return <div className="host-background-slide host-conversation-slide" aria-label={Conversation.title}>



    <style>{CONVERSATION_STYLES}</style>



    <section className={`conversation-scene ${list ? 'is-list' : 'is-caption'}`} data-scene={scene.key} data-timing-mode={timeline.mode}>



      <div className="conversation-content">



        <div className="conversation-headline" key={`${part}-${scene.key}-${clock.syncId}`}>



          <PhraseReveal text={scene.text} emphasis={scene.emphasis ?? []} clock={clock} complete={complete} />



        </div>



        {list && <ConversationRows key={`${part}-${listKind}`} kind={listKind} rows={list} clock={clock} complete={complete} />}



      </div>



    </section>



  </div>;



}







const CONVERSATION_STYLES = `



@import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@700;900&display=swap');



.host-conversation-slide {width:100%;background:#fff;color:#111;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;-webkit-font-smoothing:antialiased;}



.host-conversation-slide * {box-sizing:border-box;}



.host-conversation-slide .conversation-scene {display:flex;align-items:center;min-height:620px;padding:48px 44px;}



.host-conversation-slide .conversation-content {width:min(1000px,100%);margin:0 auto;}



.host-conversation-slide .conversation-headline h1 {margin:0;font-family:Merriweather,Georgia,serif;font-size:clamp(28px,3.2vw,40px);font-weight:700;line-height:1.25;letter-spacing:-.015em;}



.host-conversation-slide .is-caption .conversation-headline {max-width:800px;margin:auto;text-align:center;}



.host-conversation-slide .is-list .conversation-content {display:grid;grid-template-columns:minmax(230px,.85fr) minmax(330px,1.15fr);align-items:center;gap:clamp(28px,4vw,48px);}



.host-conversation-slide .is-list .conversation-headline {display:flex;align-items:center;align-self:stretch;max-width:420px;margin:0;padding-right:28px;border-right:1px solid #e5e5e5;}



.host-conversation-slide .conversation-rules {list-style:none;margin:0;padding:0;}



.host-conversation-slide .conversation-rule {position:relative;min-height:140px;padding:8px 0 24px 120px;opacity:0;transition:opacity 300ms ease;}



.host-conversation-slide .conversation-rule.is-visible {opacity:1;}



.host-conversation-slide .conversation-rule.is-muted {opacity:.3;}



.host-conversation-slide .conversation-rule-track {position:absolute;top:8px;left:0;width:100px;height:64px;}



.host-conversation-slide .conversation-rule-track::after {content:'';position:absolute;left:64px;right:4px;top:31px;height:1px;background:#dedede;}



.host-conversation-slide .conversation-rule-circle {position:relative;display:grid;place-items:center;width:64px;height:64px;border:2px dotted #bbb;border-radius:50%;color:#111;transition:border-color 300ms ease;}



.host-conversation-slide .conversation-rule-circle::before {content:'';position:absolute;inset:4px;border-radius:50%;background:#f5f5f5;}



.host-conversation-slide .conversation-rule-circle svg {position:relative;width:30px;height:30px;}



.host-conversation-slide .is-focused .conversation-rule-circle {border-color:#111;}



.host-conversation-slide .conversation-rule-dot {position:absolute;left:93px;top:28px;width:7px;height:7px;border:1.5px solid #dedede;border-radius:50%;background:#fff;}



.host-conversation-slide .conversation-rule h2 {margin:0;font-family:Merriweather,Georgia,serif;font-size:clamp(20px,2vw,25px);font-weight:700;line-height:1.3;letter-spacing:-.015em;}



.host-conversation-slide .conversation-rule p {max-width:330px;margin:10px 0 0;color:#666;font-size:14px;font-weight:400;line-height:1.55;letter-spacing:-.01em;}



.host-conversation-slide .conversation-rule:not(:last-child) .conversation-rule-copy::after {content:'';display:block;width:min(190px,75%);height:1px;margin-top:22px;background:#dedede;}



.host-conversation-slide .conversation-reveal-word {display:inline-block;opacity:0;transform:translateY(.16em);animation:conversation-word-in 280ms cubic-bezier(.18,.84,.22,1) both;}



.host-conversation-slide .conversation-reveal-word.is-emphasized {font-weight:900;}



@keyframes conversation-word-in {to{opacity:1;transform:translateY(0);}}



.host-training .ht-lesson-layout:has(.host-conversation-slide) {display:block;padding:0;}



.host-training .ht-lesson-layout:has(.host-conversation-slide)>.ht-lesson-copy {display:none;}



.host-training .ht-lesson-visual:has(.host-conversation-slide) {display:block;padding:0;background:#fff;border:0;border-radius:0;}



@media(max-width:700px){



 .host-conversation-slide .conversation-scene {min-height:530px;padding:32px 24px;}



 .host-conversation-slide .conversation-headline h1 {font-size:clamp(28px,7vw,34px);}



 .host-conversation-slide .is-list .conversation-content {grid-template-columns:1fr;gap:36px;}



 .host-conversation-slide .is-list .conversation-headline {margin:0;padding:0;border:0;}



 .host-conversation-slide .conversation-rule {min-height:144px;padding-left:102px;}



 .host-conversation-slide .conversation-rule-track {width:84px;}



 .host-conversation-slide .conversation-rule-dot {left:77px;}



 .host-conversation-slide .conversation-rule h2 {font-size:22px;}



}



@media(prefers-reduced-motion:reduce){



 .host-conversation-slide .conversation-reveal-word {animation:none!important;opacity:1;transform:none;}



 .host-conversation-slide .conversation-rule,.host-conversation-slide .conversation-rule-circle {transition:none;}



}



`;







Object.assign(Conversation, {



  "slide": 8,



  "id": "conversation",



  "title": "Be a Source of Conversation",



  "section": "Facilitation",



  "parts": [



    {



      "part": 0,



      "narration": "Now that we’ve covered how to prepare for an event and welcome guests, let’s talk about facilitation. In this section, we’ll cover how to help guests connect and keep the experience on track."



    },



    {



      "part": 1,



      "narration": "The first key concept of facilitation is to never let an event feel aimless. You’ll know it’s starting to drift when people are stuck in silence, scrolling on their phones, or hovering on the edges.\n\nTo prevent that, be ready to get a conversation going. Ask questions, share an interesting story, or bring some humor into the moment. Your energy can help people loosen up and join in."



    }



  ],



  "summary": ["Be a source of conversation.", "Ask questions. Share stories. Bring good energy."]



});
